import React, { useRef } from "react";

import Image from "next/image";
import Link from "next/link";

import { EditOutlined } from "@ant-design/icons";
import { Column, IFilter, ORDERING, Record } from "@gsmvalve/gsmvalve-table";
import { t } from "@lingui/macro";
import { Button, Row, Space, Tag, Typography } from "antd";
import { filter, groupBy, isEmpty, keyBy } from "lodash";
import get from "lodash/get";

import AppLayout from "layout/app";

import EmptyStates from "components/empty-states";
import Exports from "components/export";
import { ActionMenu } from "components/navigations/header";
import Table, { onHasFilters, pageCountMessage, TableSearch } from "components/table";

import { useApplicationContext } from "contexts/app";

import { DATA_TYPE, FILTER_CONDITION } from "@gsmvalve/gsmvalve-table/dist/cjs/types";
import { AdditionalField, Vehicle, VehicleGroup } from "types";

import useOptions from "hooks/options";

const engineChoices: { [key: string]: string } = {
  petrol: t`Petrol`,
  diesel: t`Diesel`,
  hybrid: t`Hybrid`,
  electric: t`Electric`,
  cng: t`CNG - Compressed natural gas`,
  lpg: t`LPG - Liquefied petroleum gas`,
  gas: t`Gas - Gas type other than CNG/LPG`,
  petrol_hybrid: t`Petrol hybrid`,
  diesel_hybrid: t`Diesel hybrid`,
  cng_hybrid: t`CNG hybrid`,
  lpg_hybrid: t`LPG hybrid`,
};
const getEngineChoices = (key: string) => {
  const chosen = engineChoices[key];
  if (chosen) {
    return chosen;
  }

  return key;
};

const limit = 100;
const sideload = ["accounts", "drivers", "services_upcoming", "services_previous", "users", "vehicle_groups"];
const excludeExports = ["api"];
const excludedVehicleGroup = ["default"];

interface Services {
  services: { id: string; name: string }[];
}

const Services = ({ services }: Services) => {
  return (
    <Space direction="vertical">
      {services?.map((service) => (
        <div key={service.id}>
          <Link href={`/services/${service.id}/edit/`}>{service.name}</Link>
        </div>
      ))}
    </Space>
  );
};

const modifier = (accountURL: string) => (data: any) => {
  const records: Record[] = [];

  data?.forEach((rows: any) => {
    const row = rows?.data;
    const accounts = keyBy(row?.accounts, "url");
    const drivers = keyBy(row?.drivers, "url");
    const users = keyBy(row?.users, "url");
    const servicesUpcoming = groupBy(row?.services_upcoming, "vehicle");
    const servicesPrevious = groupBy(row?.services_previous, "vehicle");
    const vehicleGroups = row?.vehicle_groups.filter(
      (group: VehicleGroup) => !excludedVehicleGroup.includes(group?.group_type as any) && group.account === accountURL
    );
    const keyedVehicleGroups = keyBy(vehicleGroups, "url");

    row?.vehicles?.forEach((vehicle: any) => {
      records.push({
        ...vehicle,
        account: accounts[vehicle.account]?.display_name,
        driver_name: drivers[vehicle.driver]?.name,
        user: users[vehicle.user]?.name_display,
        accountable_user: users[vehicle.accountable_user]?.name_display,
        engine_displacement: vehicle.engine_displacement,
        vehicle_groups: vehicle.vehicle_groups
          ?.map((group: string | number) => {
            return keyedVehicleGroups[group]?.name_display;
          })
          .filter(Boolean)
          .join(", "),
        services_upcoming: servicesUpcoming[vehicle.url],
        services_previous: servicesPrevious[vehicle.url],
      });
    });
  });

  return records;
};

const filters = (client: any, account: string, basePath?: string): IFilter[] => {
  return [
    { key: "registration", title: t`Name`, supported: [FILTER_CONDITION.EQUALS] },
    {
      key: "accountable_user",
      title: t`Accountable user`,
      type: DATA_TYPE.CHOICE,
      condition: FILTER_CONDITION.IN,
      autoComplete: {
        url: `${basePath}users/?account=${account}`,
        label: "Search",
        mode: "multiple",
        valueKey: "id",
        labelKey: "name_display",
        client: client,
      },
    },
    { key: "make", title: t`Make`, supported: [FILTER_CONDITION.EQUALS] },
    { key: "model", title: t`Model`, supported: [FILTER_CONDITION.EQUALS] },
    { key: "manufacturing_year", title: t`Manufacturing year`, supported: [FILTER_CONDITION.EQUALS] },
    {
      key: "fuel_type",
      title: t`Fuel type`,
      supported: [FILTER_CONDITION.EQUALS],
      type: DATA_TYPE.CHOICE,
      options: Object.keys(engineChoices)?.map((key: string) => {
        return {
          value: key,
          label: engineChoices[key],
        };
      }),
    },
    { key: "vin", title: t`VIN`, supported: [FILTER_CONDITION.EQUALS] },
    {
      key: "driver",
      title: t`Driver`,
      type: DATA_TYPE.CHOICE,
      condition: FILTER_CONDITION.IN,
      autoComplete: {
        url: `${basePath}drivers/?account=${account}&active=true`,
        label: "Search",
        mode: "multiple",
        valueKey: "id",
        labelKey: "name",
        client: client,
      },
    },
    {
      key: "vehicle_group",
      title: t`Vehicle group`,
      type: DATA_TYPE.CHOICE,
      condition: FILTER_CONDITION.IN,
      autoComplete: {
        url: `${basePath}vehicle_groups/?account=${account}&active=true`,
        label: "Search",
        mode: "multiple",
        valueKey: "id",
        labelKey: "name_display",
        client: client,
      },
    },
    { key: "type", title: t`Type`, supported: [FILTER_CONDITION.EQUALS] },
    { key: "color", title: t`Color`, supported: [FILTER_CONDITION.EQUALS] },
    { key: "engine_displacement", title: t`Engine displacement`, supported: [FILTER_CONDITION.EQUALS] },
    { key: "first_registration_date", omit: true, title: "" },
    { key: "average_fuel_consumption", title: t`Average fuel consumption`, supported: [FILTER_CONDITION.EQUALS] },
    { key: "average_co2_emission", title: t`Average CO2 emission`, supported: [FILTER_CONDITION.EQUALS] },
    { key: "trip_tariff", title: t`Trip tariff`, supported: [FILTER_CONDITION.EQUALS] },
    { key: "leasing_start", omit: true, title: "" },
    { key: "leasing_end", omit: true, title: "" },
    {
      key: "leasing_period_fee",
      title: t`Leasing period fee`,
      supported: [FILTER_CONDITION.EQUALS],
      label: `Leasing information`,
    },
    {
      key: "leasing_period_mileage",
      title: t`Leasing period mileage`,
      supported: [FILTER_CONDITION.EQUALS],
      label: `Leasing information`,
    },
    { key: "previous_services", omit: true, title: "" },
    { key: "upcoming_services", omit: true, title: "" },
    { key: "account", omit: true, title: "" },
    { key: "primary_account", omit: true, title: "" },
    { key: "permissions", omit: true, title: "" },
    { key: "managed_permissions", omit: true, title: "" },
    { key: "additional_fields", omit: true, title: "" },
    { key: "vehicle_groups", omit: true, title: "" },
    { key: "display", omit: true, title: "" },
    { key: "name", omit: true, title: "" },
  ];
};

const Vehicles = () => {
  const tableCallback = useRef<any>(null);
  const { session, configuration, httpClient } = useApplicationContext();

  const accountURL = get(configuration, "account.url", "");
  const optionsUrl = "reports/vehicles/";
  const { result: options, loading } = useOptions(optionsUrl, "");
  const url = `vehicles/?account=${session?.accountId}&permissions=can_manage&count=true&sideload=${sideload}&active=true`;
  const columns: Column[] = [
    {
      key: "name_display",
      title: t`Name`,
      isVisible: true,
      fixed: true,
      width: 250,
      dataIndex: ["name_display"],
      type: DATA_TYPE.CUSTOM,
      render: (data) => {
        return <Link href={`/settings/vehicles/${data?.id}`}>{data?.name_display}</Link>;
      },
      isSortable: true,
      export_key: "name", // TODO: convert to camel case
    },
    {
      key: "registration",
      title: t`Registration`,
      isVisible: true,
      type: DATA_TYPE.CUSTOM,
      width: 250,
      render: (data) => {
        return <Link href={`/settings/vehicles/${data?.id}`}>{data?.registration}</Link>;
      },
      isSortable: true,
    },
    {
      key: "make",
      title: t`Make`,
      isVisible: true,
      isSortable: true,
    },
    { key: "model", title: t`Model`, isVisible: true, isSortable: true },
    {
      key: "manufacturing_year",
      title: t`Manufacturing year`,
      type: DATA_TYPE.TEXT,
      isVisible: true,
      isSortable: true,
    },
    {
      key: "driver_name",
      title: t`Driver`,
      isVisible: true,
      dataIndex: ["driver_name"],
      type: DATA_TYPE.TEXT,
      exportable: true,
      export_key: "driver_name",
      defaultIfNull: "-",
    },
    {
      key: "fuel_type",
      title: t`Fuel type`,
      isVisible: true,
      render: (data: { fuel_type: string }) => {
        if (isEmpty(data.fuel_type)) {
          return "-";
        }
        return <Typography.Text>{getEngineChoices(data.fuel_type)}</Typography.Text>;
      },
      isSortable: true,
    },
    {
      key: "active",
      title: t`Active`,
      isVisible: true,
      isSortable: true,
      width: 100,
      render: (data) => <Tag color={data.active ? "green" : undefined}>{data.active ? t`Active` : t`Inactive`}</Tag>,
    },
    {
      key: "upcoming_service_names",
      title: t`Upcoming services`,
      isVisible: false,
      width: 200,
      export_key: "upcoming_service_names",
      exportable: true,
      render: (data) => {
        const services = data?.services_upcoming;

        if (services) {
          return <Services services={services} />;
        }

        return "-";
      },
    },
    {
      key: "previous_service_names",
      title: t`Previous services`,
      isVisible: false,
      width: 200,
      export_key: "previous_service_names",
      exportable: true,
      render: (data) => {
        const services = data?.services_previous;

        if (services) {
          return <Services services={services} />;
        }

        return "-";
      },
    },
    { key: "vin", title: t`VIN`, isVisible: false, isSortable: true },
    {
      key: "vehicle_groups",
      title: t`Vehicle Groups`,
      width: 200,
      isSortable: true,
      exportable: true,
      export_key: "vehicle_group_names",
    },
    {
      key: "accountable_user",
      title: t`Accountable user`,
      isSortable: true,
      exportable: true,
      isVisible: false,
      export_key: "accountable_user_name",
      render: (data) => {
        const user = data?.accountable_user;
        if (user) {
          return <div>{user}</div>;
        }

        return "-";
      },
    },
    { key: "workhours", title: t`Work hours`, isSortable: true },
    { key: "fuel_tank_capacities", title: t`Fuel tank capacities`, isSortable: true },
    { key: "fuel_tanked_thresholds", title: t`Fuel tanked thresholds`, isSortable: true },
    { key: "fuel_removed_thresholds", title: t`Fuel removed thresholds`, isSortable: true },
    {
      key: "engine_displacement",
      title: t`Engine displacement`,
      isSortable: true,
      render: (data) => {
        if (data.engine_displacement === null || data.engine_displacement === undefined) {
          return "-";
        }

        return <div>{data.engine_displacement}</div>;
      },
    },

    {
      key: "switched_by_ignition",
      title: t`Switched by ignition`,
      render: (data) => (
        <Tag color={data.switched_by_ignition ? "green" : undefined}>
          {data.switched_by_ignition ? t`Active` : t`Inactive`}
        </Tag>
      ),
    },
    {
      key: "last_activity_enabled",
      title: t`Last activity enabled`,
      render: (data) => (
        <Tag color={data.last_activity ? "green" : undefined}>{data.last_activity ? t`Active` : t`Inactive`}</Tag>
      ),
    },
    { key: "leasing_end", title: t`Leasing end`, group: t`Leasing information`, isSortable: true },
    { key: "leasing_start", title: t`Leasing start`, group: t`Leasing information`, isSortable: true },
    { key: "leasing_period_fee", title: t`Leasing period fee`, group: t`Leasing information`, isSortable: true },
    {
      key: "leasing_period_mileage",
      title: t`Leasing period mileage`,
      group: t`Leasing information`,
      isSortable: true,
    },

    { key: "color", title: t`Color` },
    { key: "accountable_user", title: t`Accountable user` },
    {
      key: "average_co2_emission",
      title: t`Average CO2 emission`,
      render: (data) => {
        if (data.average_co2_emission === null) {
          return "-";
        }

        return <Typography.Text>{data.average_co2_emission} g/km</Typography.Text>;
      },
    },
    { key: "average_fuel_consumption", title: t`Average fuel consumption` },
    { key: "country", title: t`Country` },
    {
      key: "country_split_enabled",
      title: t`Country split enabled`,
      render: (data) => (
        <Tag color={data.country_split_enabled ? "green" : undefined}>
          {data.country_split_enabled ? t`Active` : t`Inactive`}
        </Tag>
      ),
    },
    {
      key: "daily_split_times_enabled",
      title: t`Daily split times enabled`,
      render: (data) => (
        <Tag color={data.daily_split_times_enabled ? "green" : undefined}>
          {data.daily_split_times_enabled ? t`Active` : t`Inactive`}
        </Tag>
      ),
    },
    {
      key: "eco_score_enabled",
      title: t`Eco score enabled`,
      render: (data) => (
        <Tag color={data.eco_score_enabled ? "green" : undefined}>
          {data.eco_score_enabled ? t`Active` : t`Inactive`}
        </Tag>
      ),
    },
    { key: "error_state_threshold", title: t`Error state threshold` },
    { key: "first_registration_date", title: t`First registration date` },
    {
      key: "fuel_events_enabled",
      title: t`Fuel events enabled`,
      render: (data) => (
        <Tag color={data.fuel_events_enabled ? "green" : undefined}>
          {data.fuel_events_enabled ? t`Active` : t`Inactive`}
        </Tag>
      ),
    },
    { key: "fuel_removed_threshold", title: t`Fuel removed threshold` },
    { key: "fuel_tank_capacities", title: t`Fuel tank capacities` },
    { key: "fuel_tanked_threshold", title: t`Fuel tanked threshold` },
    { key: "fuel_used_source", title: t`Fuel used source` },
    {
      key: "history_optimization_enabled",
      title: t`History optimizations enabled`,
      render: (data) => (
        <Tag color={data.history_optimization_enabled ? "green" : undefined}>
          {data.history_optimization_enabled ? t`Active` : t`Inactive`}
        </Tag>
      ),
    },
    {
      key: "tachograph_enabled",
      title: t`Tachograph enabled`,
      render: (data) => (
        <Tag color={data.tachograph_enabled ? "green" : undefined}>
          {data.tachograph_enabled ? t`Active` : t`Inactive`}
        </Tag>
      ),
    },
    {
      key: "fuel_private_trips",
      title: t`Fuel private trips`,
      render: (data) => (
        <Tag color={data.fuel_private_trips ? "green" : undefined}>
          {data.fuel_private_trips ? t`Active` : t`Inactive`}
        </Tag>
      ),
    },
    {
      key: "hide_private_trips",
      title: t`Hide private trips`,
      render: (data) => (
        <Tag color={data.hide_private_trips ? "green" : undefined}>
          {data.hide_private_trips ? t`Active` : t`Inactive`}
        </Tag>
      ),
    },
    { key: "tachograph_locked_at", title: t`Tachograph locked at` },
    {
      key: "total_distance_enabled",
      title: t`Total distance enabled`,
      render: (data) => (
        <Tag color={data.total_distance_enabled ? "green" : undefined}>
          {data.total_distance_enabled ? t`Active` : t`Inactive`}
        </Tag>
      ),
    },
    {
      key: "total_engine_time_enabled",
      title: t`Total engine time enabled`,
      render: (data) => (
        <Tag color={data.total_engine_time_enabled ? "green" : undefined}>
          {data.total_engine_time_enabled ? t`Active` : t`Inactive`}
        </Tag>
      ),
    },
    { key: "trip_tariff", title: t`Trip tariff` },
    { key: "type", title: t`Type` },

    {
      key: "actions",
      title: t`Actions`,
      width: 90,
      render: (data) => {
        return (
          <Link href={`/settings/vehicles/${data?.id}`}>
            <Button type={"text"} icon={<EditOutlined />} />
          </Link>
        );
      },
      isVisible: false,
    },
    { key: "secondary_accounts", title: t`Secondary contacts`, omit: true },
    { key: "name", omit: true },
    { key: "url", omit: true },
    { key: "account", omit: true },
    { key: "user", omit: true },
    { key: "auth_tachograph_card", omit: true },
    { key: "tachograph_lock_card", omit: true },
    { key: "driver", omit: true },
  ];

  const additionalFields: AdditionalField[] | undefined = get(configuration, "additional_fields");
  const vehicleAdditionalFields = additionalFields?.filter((field: AdditionalField) => {
    return field.available?.includes("vehicles") && field.active;
  });

  const vehicleAdditionalFieldsColumns: any = vehicleAdditionalFields?.map((column: AdditionalField) => {
    return {
      key: column.slug,
      title: column.name,
      dataIndex: ["additional_fields", column.slug],
      type: DATA_TYPE.CUSTOM,
      group: column.category || t`Additional fields`,
      render: (data: any) => {
        if (isEmpty(data?.additional_fields)) {
          return "-";
        }

        return <Typography.Text>{get(data?.additional_fields, `${column.slug}`)}</Typography.Text>;
      },
      isSortable: true,
      export_key: column?.field_name,
    };
  });

  const vehicleAdditionalFieldsFilters: any = vehicleAdditionalFields?.map((additionalField: AdditionalField) => {
    const row: IFilter = {
      key: additionalField.slug || "",
      title: additionalField.name,
      supported: [FILTER_CONDITION.EQUALS],
      type: DATA_TYPE.TEXT,
      modifyFilter: (key, value, condition) => {
        return { key: "additional_fields__contains", value: `${additionalField.slug}:${value}` };
      },
      removeByKeyInValue: "additional_fields__contains",
    };

    if (!isEmpty(additionalField.choices)) {
      row["multipleMode"] = false;
      row["type"] = DATA_TYPE.CHOICE;
      row["condition"] = FILTER_CONDITION.EQUALS;
      row["options"] = additionalField.choices?.map((key: string) => {
        return {
          value: key,
          label: key,
        };
      });
    }

    return row;
  });

  return (
    <>
      <AppLayout pageTitle={t`Vehicles`} fullscreen={true}>
        <ActionMenu
          menus={[
            <Row>
              <TableSearch placeholder={t`Search vehicles...`} />
            </Row>,
            <Exports
              optionsUrl={optionsUrl}
              formats={filter(options?.formats, (f) => !excludeExports.includes(f.value))}
              loading={loading}
              callback={(fn: any) => {
                if (tableCallback?.current) {
                  tableCallback?.current((columns: string[]) => {
                    fn(columns);
                  });
                }
              }}
              ordering={["name_display", "id"]}
            />,
          ]}
        />
        <Table<Vehicle[]>
          key={"vehicles"}
          id={`${session?.accountId}-vehicles`}
          columns={{
            filtersPath: "/vehicles/",
            path: "Vehicle",
            customColumns: columns.concat(vehicleAdditionalFieldsColumns),
            customFilters: [
              ...filters(httpClient.get, String(session?.accountId), httpClient.basePath),
              ...vehicleAdditionalFieldsFilters,
            ],
          }}
          records={{
            url: url,
            sort: [{ key: "name_display", order: "asc" as ORDERING }],
            limit: limit,
            modifier: modifier(accountURL),
          }}
          tabsFilter={{
            key: "active",
            default: "true",
            filters: [
              { value: "true", label: t`Active` },
              { value: "false", label: t`Inactive` },
              { value: "", label: t`All` },
            ],
          }}
          callback={tableCallback}
          empty={{
            emptyWithoutFilter: (
              <EmptyStates
                title={t`No vehicle yet`}
                description={t`All vehicles that you can view and manage will appear here.`}
                icon={
                  <Image
                    src={"/images/vehicle-empty-states.svg"}
                    width={260}
                    // @ts-ignore
                    height={260}
                    alt={t`vehicle empty state`}
                  />
                }
              />
            ),
            onHasFilter: onHasFilters(),
          }}
          controls={{
            manageColumn: true,
            tabSort: true,
            filterBtn: true,
            pageCountMessage: pageCountMessage(t`Vehicles`),
          }}
        />
      </AppLayout>
    </>
  );
};

export default Vehicles;
